
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSpotOwner } from "@/hooks/useSpotOwner";
import EditParkingSpotForm from "./EditParkingSpotForm";
import SpotDetails from "./SpotDetails";
import SpotCardHeader from "./SpotCardHeader";
import BookingDialog from "./BookingDialog";
import { useBooking } from "@/hooks/useBooking";
import { useNavigate } from "react-router-dom";

interface ParkingSpot {
  id: string;
  owner_id: string;
  owner_name: string;
  address: string;
  vehicle_type: string;
  security_type: string;
  price_per_hour: number;
  parking_lot_count: number;
  images: string[];
  is_available: boolean;
}

interface ParkingSpotCardProps {
  spot: ParkingSpot;
}

const ParkingSpotCard = ({ spot: initialSpot }: ParkingSpotCardProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(initialSpot);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isOwner } = useSpotOwner(spot.owner_id);
  const { handleBooking } = useBooking();

  useEffect(() => {
    if (isOwner) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user || user.id === spot.owner_id) return;
      supabase.from("spot_views").insert({ parking_spot_id: spot.id, viewer_id: user.id });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot.id, isOwner]);

  const handleAvailabilityChange = async (value: string) => {
    if (!isOwner) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('parking_spots')
        .update({ 
          is_available: value === 'online'
        })
        .eq('id', spot.id);

      if (error) throw error;

      // Update local state
      setSpot(prev => ({
        ...prev,
        is_available: value === 'online'
      }));

      toast({
        title: "Success",
        description: `Parking spot is now ${value}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleEditSuccess = async () => {
    // Fetch the updated spot data
    const { data, error } = await supabase
      .from('parking_spots')
      .select('*')
      .eq('id', spot.id)
      .single();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to refresh spot data",
        variant: "destructive",
      });
    } else if (data) {
      // Update the local state with the fresh data
      setSpot(data);
    }

    setIsEditing(false);
  };

  const handleDelete = () => {
    // Navigate to My Listings page after successful deletion
    navigate('/my-listings');
  };

  const handleBookClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (isEditing && isOwner) {
    return (
      <EditParkingSpotForm
        spot={spot}
        onCancel={() => setIsEditing(false)}
        onSuccess={handleEditSuccess}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <Card>
      <SpotCardHeader address={spot.address} spotId={spot.id} />
      <CardContent>
        <BookingDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onBook={(date, time, hours, vehicleNumber, lotCount) =>
            handleBooking(spot.id, spot.price_per_hour, date, time, hours, vehicleNumber, lotCount)
          }
          parkingSpotId={spot.id}
          maxLots={spot.parking_lot_count}
        >
          <SpotDetails
            spot={spot}
            isOwner={isOwner}
            isUpdating={isUpdating}
            onAvailabilityChange={handleAvailabilityChange}
            onEditClick={handleEditClick}
            onBookClick={handleBookClick}
            setDialogOpen={setDialogOpen}
          />
        </BookingDialog>
      </CardContent>
    </Card>
  );
};

export default ParkingSpotCard;
